import React, { useEffect, useRef, useState } from "react";
import { ShoppingBag, BellRing, XCircle, X } from "lucide-react";

const TOAST_CONFIG = {
    new_order: {
        icon: ShoppingBag,
        label: "New Order Placed",
        color: "#6366f1",       // .order-count-badge
        border: "#e0e0ff",
    },
    ready_order: {
        icon: BellRing,
        label: "Order Ready for Pickup",
        color: "#10b981",       // .order-price / .completed
        border: "#d1fae5",
    },
    cancelled: {
        icon: XCircle,
        label: "Order Cancelled",
        color: "#e65d5d",       // .order-cancelled-count
        border: "#ffe0e0",
    },
};
const Toast = ({ id, status, orderId, onDismiss }) => {
    const cfg = TOAST_CONFIG[status] ?? TOAST_CONFIG.new_order;
    const Icon = cfg.icon;
    const [visible, setVisible] = useState(false);

    // slide-in on mount
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 20);
        return () => clearTimeout(t);
    }, []);

    const handleClose = () => {
        setVisible(false);
        setTimeout(() => onDismiss(id), 350);
    };

    return (
        <div
            style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: 320,
                borderRadius: 12,
                padding: "14px 36px 14px 14px",
                background: "#ffffff",
                border: `1px solid ${cfg.border}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.06)",
                overflow: "hidden",
                pointerEvents: "all",
                fontFamily: "'Inter', sans-serif",
                transform: visible ? "translateX(0)" : "translateX(calc(100% + 30px))",
                opacity: visible ? 1 : 0,
                transition: "transform 0.38s cubic-bezier(.22,1,.36,1), opacity 0.3s ease",
            }}
        >
            {/* Left accent bar — mirrors .order-card.order-ready */}
            <div style={{
                position: "absolute",
                left: 0, top: 0, bottom: 0,
                width: 4,
                borderRadius: "12px 0 0 12px",
                background: cfg.color,
            }} />

            {/* Icon bubble — mirrors .status-icon-wrapper */}
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: `${cfg.color}15`,
                marginLeft: 6,
            }}>
                <Icon size={20} color={cfg.color} />
            </div>

            {/* Text — mirrors .count / .label */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}>
                <span style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#1a1a1a",
                    lineHeight: 1.2,
                }}>
                    {cfg.label}
                </span>
                <span style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "#71717a",
                    letterSpacing: "0.05em",
                    fontFamily: "monospace",
                }}>
                    #{String(orderId).slice(-8)}
                </span>
            </div>

            {/* Close button — mirrors .toggle-button */}
            <button
                onClick={handleClose}
                title="Dismiss"
                style={{
                    position: "absolute",
                    top: 8, right: 8,
                    background: "#f1f5f9",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 6,
                    transition: "background 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#e2e8f0"}
                onMouseLeave={e => e.currentTarget.style.background = "#f1f5f9"}
            >
                <X size={13} color="#475569" />
            </button>
        </div>
    );
};


export default function NotificationToaster({ updatedOrders = [], onDismissOrder }) {
    const [exiting, setExiting] = useState(new Set());
    const prevIdsRef = useRef(new Set());

    // Detect orders removed from updatedOrders externally → trigger slide-out
    useEffect(() => {
        const currentIds = new Set(updatedOrders.map(o => o.id));

        prevIdsRef.current.forEach(prevId => {
            if (!currentIds.has(prevId)) {
                // Gone from parent — start exit animation
                setExiting(ex => {
                    const next = new Set(ex);
                    next.add(prevId);
                    return next;
                });
                // Clean up exiting set after animation
                setTimeout(() => {
                    setExiting(ex => {
                        const next = new Set(ex);
                        next.delete(prevId);
                        return next;
                    });
                }, 400);
            }
        });

        prevIdsRef.current = currentIds;
    }, [updatedOrders]);

    // What to render: current updatedOrders + any still-animating-out ones
    const visibleOrders = updatedOrders.filter(o => !exiting.has(o.id));

    const handleDismiss = (orderId) => {
        // Tell parent to remove from updatedOrders
        onDismissOrder(orderId);
    };

    if (visibleOrders.length === 0) return null;

    return (
        <div style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            pointerEvents: "none",
        }}>
            {visibleOrders.map((o) => (
                <Toast
                    key={o.id}
                    id={o.id}
                    status={o.status}
                    orderId={String(o.id)}
                    onDismiss={handleDismiss}
                />
            ))}
        </div>
    );
}